import { clientConfig } from '@/config/clientConfig';
import { MirStatusBadge } from '@/domains/jobs/components/MirStatusBadge';
import { PSRBadge } from '@/domains/jobs/components/PSRBadge';
import { RejectPackageModal } from '@/domains/jobs/components/RejectPackageModal';
import { RequestMoreInfoButton } from '@/domains/jobs/components/RequestMoreInfoButton';
import { RequestMoreInfoOrReplyModal } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import type { RequestMoreInfoOrReplyJob } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import { ValidationRequestModal } from '@/domains/jobs/components/ValidationRequestModal';
import { canCancelJob } from '@/domains/jobs/constants/canCancelJob';
import {
  useAbortWorkOrder,
  useAcceptWorkOrders,
  useCancelFromAbort,
  useCancelWorkOrder,
  useEngineerEnRoute,
  useEngineerOnSite,
  useEngineeringComplete,
  useRejectWorkOrders,
  useReplanFromAbort,
  useReplyMoreInfoWorkOrders,
  useRequestMoreInfoWorkOrders,
  useValidationRequested,
} from '@/domains/jobs/hooks/useWorkOrderActions';
import { uploadJobDocuments } from '@/domains/jobs/lib/uploadJobDocuments';
import type { ConversationMessage } from '@/domains/jobs/types/workOrder';
import { queryKeys } from '@/lib/query-client';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/ui/utils';
import {
  PERMIT_CATEGORY,
  PERMIT_STATUS,
  isPermitOrderType,
  permitProgressionUngated,
} from '@skewb-works/contracts';
import type { JobPermit, PermitCategory } from '@skewb-works/contracts';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  RefreshCcw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

export interface StatusBarJobData {
  id: string;
  status: string;
  priority: string;
  hasResponse?: boolean;
  hasBeenResent?: boolean;
  externalId?: string | null;
  name?: string | null;
  address?: string | null;
  reqCompletionDate?: string | null;
  mirPendingDirection?: 'inbound' | 'outbound' | null;
  mirConversationHistory?: ConversationMessage[];
  jobStatusName?: string | null;
  isVulnerable?: boolean;
  orderType?: string | null;
  permitCategory?: PermitCategory | null;
  permit?: JobPermit | null;
  mandatoryWorkOutstanding?: boolean;
}

/**
 * Permit gating on the engineer status transitions (WRK-5704). Among
 * permit-bearing order types (ZRP1 / ZRC2) the permit category CORE resolves
 * after the SAP DISP confirmation drives the workflow.
 *
 * The order type is the outer guard: only ZRP1 / ZRC2 have a permit at all, so
 * any other order type (ZOS1, ZOS2, ZRP2, ZBF1, …) skips every permit gate and
 * follows the standard lifecycle. Without this a non-permit work order would
 * hang at Scheduled forever waiting for a Permit Category event CORE never
 * sends for it.
 *
 * Per the "CORE ↔ Works Lite Permit Flow Changes" doc, for permit order types:
 *   Before the Permit Category event → do NOT allow Engineer En Route.
 *   planned/immediate → En Route only once permit details are received.
 *   planned/immediate → no Technical Completion until the permit is stopped.
 *   none → standard lifecycle, no gating.
 *
 * Exception (WRK-5786, narrowed by WRK-5885, restored in full by CWL-125): ZRP1 +
 * immediate is ungated for EVERY gate — the permit may arrive late or never, so
 * the work order must carry on as normal without it. permitProgressionUngated()
 * (shared with the backend gate) collapses that one combination to the standard
 * lifecycle. ZRC2 and planned ZRP1 keep full gating throughout.
 *
 * WRK-9999 override: completed mandatory forms bypass the permit-stopped rule
 * on Technical Completion — see the matching change in the backend gate
 * (permitGate.ts's getTechnicalCompletionPermitState). This mirror kept its own
 * copy of the OLD unconditional rule for a while after the backend was fixed —
 * a job with forms complete but no permit showed blocked in the UI even though
 * the API call would have succeeded. `job.mandatoryWorkOutstanding` comes from
 * the same findOutstandingMandatoryWork check the backend gate now runs
 * (job-detail's `mandatory_work_outstanding`), so the two cannot drift apart.
 * Still load-bearing for planned permits and for ZRC2, which the CWL-125 ungate
 * does not cover.
 *
 * Returns a reason string when the transition is blocked, or null when allowed.
 */
type PermitGate = { blocked: true; reason: string } | { blocked: false };

function getEnRouteGate(job: StatusBarJobData): PermitGate {
  // Non-permit order types have no permit workflow — never gate them.
  if (!isPermitOrderType(job.orderType)) return { blocked: false };

  const category = job.permitCategory ?? null;

  // ZRP1 + immediate progresses without waiting on the permit (see comment above).
  if (permitProgressionUngated(job.orderType, category)) return { blocked: false };

  if (category == null || category === PERMIT_CATEGORY.PENDING) {
    return { blocked: true, reason: 'Waiting for the permit category from CORE.' };
  }

  // planned and immediate both wait for the permit details (the arranged /
  // details-received event) before the engineer can go en route — the category
  // alone is not enough (per the flow doc).
  if (
    (category === PERMIT_CATEGORY.PLANNED || category === PERMIT_CATEGORY.IMMEDIATE) &&
    !job.permit
  ) {
    return {
      blocked: true,
      reason: 'Waiting for permit details before the engineer can go en route.',
    };
  }

  return { blocked: false };
}

export function getTechnicalCompletionGate(job: StatusBarJobData): PermitGate {
  // Non-permit order types have no permit lifecycle to complete — never gate them.
  if (!isPermitOrderType(job.orderType)) return { blocked: false };

  const category = job.permitCategory ?? null;

  // ZRP1 + immediate carries on as normal, permit or no permit (CWL-125) — the
  // same shortcut the En Route gate and the backend apply.
  if (permitProgressionUngated(job.orderType, category)) return { blocked: false };

  if (category !== PERMIT_CATEGORY.PLANNED && category !== PERMIT_CATEGORY.IMMEDIATE)
    return { blocked: false };

  // WRK-9999: every mandatory form already done overrides the permit-stopped
  // rule — see the class comment above and permitGate.ts on the backend.
  if (job.mandatoryWorkOutstanding === false) return { blocked: false };

  if (job.permit?.status !== PERMIT_STATUS.STOPPED) {
    return { blocked: true, reason: 'The permit must be stopped before technical completion.' };
  }

  return { blocked: false };
}

function PermitGateNotice({ reason }: { reason: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <ShieldAlert className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <span>{reason}</span>
    </div>
  );
}

function normalizeStatus(status: string): string {
  const lower = status.toLowerCase().trim();
  if (lower === 'more info requested') return 'more-info-requested';
  if (lower === 'in progress') return 'in-progress';
  return lower.replace(/\s+/g, '-');
}

function toTitleCase(status: string): string {
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
      onClick={onClick}
    >
      <XCircle className="w-4 h-4 mr-2" />
      Cancel
    </Button>
  );
}

function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'text-red-600 border-red-600 bg-red-50 dark:bg-red-950/30';
    case 'medium':
      return 'text-amber-600 border-amber-600 bg-amber-50 dark:bg-amber-950/30';
    case 'low':
      return 'text-slate-600 border-slate-600 bg-slate-50 dark:bg-slate-950/30';
    // 'Unknown' means the priority was never specified, so it gets a dashed
    // border to read as "not set" rather than looking like a real, low-urgency
    // priority — it was being mistaken for Low (SC-6369).
    case 'unknown':
      return 'text-gray-500 border-gray-400 border-dashed dark:text-gray-400 dark:border-gray-600';
    default:
      return 'text-slate-600 border-slate-600';
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 border-green-600">
          Completed
        </Badge>
      );
    case 'in-progress':
    case 'accepted':
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-600">
          In Progress
        </Badge>
      );
    case 'new':
      return (
        <Badge className="bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100 border-slate-600">
          New
        </Badge>
      );
    case 'reopened':
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-600">
          Reopened
        </Badge>
      );
    case 'more-info-requested':
      return (
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-600">
          More Info Requested
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 border-red-600">
          Rejected
        </Badge>
      );
    case 'urgent':
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 border-red-600">
          Urgent
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-600">
          Pending
        </Badge>
      );
    default:
      return <Badge variant="outline">{toTitleCase(status)}</Badge>;
  }
}

export function StatusBar({ jobData }: { jobData: StatusBarJobData }) {
  const navigate = useNavigate();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [engineeringCompleteModalOpen, setEngineeringCompleteModalOpen] = useState(false);
  const [engineeringCompleteNote, setEngineeringCompleteNote] = useState('');
  const [validationRequestedModalOpen, setValidationRequestedModalOpen] = useState(false);
  const [abortModalOpen, setAbortModalOpen] = useState(false);
  const [abortNote, setAbortNote] = useState('');
  const [cancelFromAbortModalOpen, setCancelFromAbortModalOpen] = useState(false);
  const [replanFromAbortModalOpen, setReplanFromAbortModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const acceptWorkOrders = useAcceptWorkOrders();
  const engineerEnRoute = useEngineerEnRoute();
  const engineerOnSite = useEngineerOnSite();
  const engineeringComplete = useEngineeringComplete();
  const validationRequested = useValidationRequested();
  const abortWorkOrder = useAbortWorkOrder();
  const cancelFromAbort = useCancelFromAbort();
  const replanFromAbort = useReplanFromAbort();
  const cancelWorkOrderMutation = useCancelWorkOrder();
  const rejectWorkOrders = useRejectWorkOrders();
  const requestMoreInfo = useRequestMoreInfoWorkOrders();
  const replyMoreInfo = useReplyMoreInfoWorkOrders();
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const status = normalizeStatus(jobData.status);
  // Permit gates (WRK-5704): En Route is held until the permit category /
  // details are received; Technical Completion is held until the permit is
  // stopped. Both no-op when the job has no permit workflow.
  const enRouteGate = getEnRouteGate(jobData);
  const technicalCompletionGate = getTechnicalCompletionGate(jobData);
  const isReply = jobData.mirPendingDirection === 'inbound';
  const wos = jobData.status;
  const js = jobData.jobStatusName ?? '';
  // Cancel is a post-acceptance action only; canCancelWorkOrder mirrors the
  // backend OUTBOUND_CANCEL_ALLOWED list, so a Not Started / New | Reopened job
  // (awaiting Accept/Reject) never shows Cancel.
  const showCancelButton = canCancelJob(js, wos);

  const requestInfoJob: RequestMoreInfoOrReplyJob = {
    id: Number(jobData.id),
    name: jobData.name ?? null,
    address: jobData.address ?? null,
    work_order_status: jobData.status,
    req_completion_date: jobData.reqCompletionDate ?? null,
    mir_pending_direction: jobData.mirPendingDirection ?? null,
  };

  const handleRequestInfoSubmit = async (comment: string, files: File[]) => {
    setIsUploadingFiles(true);
    try {
      const jobId = Number(jobData.id);

      const attachments = files.length > 0 ? await uploadJobDocuments(jobId, files) : [];
      if (attachments.length > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.documents(String(jobId)) });
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.activityLog(String(jobId)) });
      }

      const payload = [
        {
          id: jobId,
          externalId: jobData.externalId ?? '',
          comments: comment,
          attachments,
        },
      ];

      if (isReply) {
        await replyMoreInfo.mutateAsync(payload);
      } else {
        await requestMoreInfo.mutateAsync(payload);
      }
      setRequestInfoModalOpen(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(jobData.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.milestoneHistory(jobData.id) });
    } catch (error) {
      console.error('Error handling request info submit:', error);
      showErrorToast('Submission failed', 'Something went wrong. Please try again.');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleRejectSubmit = async (reason: string, files?: File[]) => {
    setIsUploadingFiles(true);
    try {
      const jobId = Number(jobData.id);

      // Upload supporting documents first so they persist as job_file rows
      // (visible in Lite) and produce the attachment refs we forward to CORE.
      const attachments = files?.length ? await uploadJobDocuments(jobId, files) : [];
      if (attachments.length > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.documents(String(jobId)) });
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.activityLog(String(jobId)) });
      }

      // Success toast comes from the mutation's meta (via the global
      // MutationCache handler), matching the request-more-info flow.
      await rejectWorkOrders.mutateAsync([
        {
          id: jobId,
          externalId: jobData.externalId ?? '',
          comments: reason,
          attachments,
        },
      ]);

      setRejectModalOpen(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(jobData.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.milestoneHistory(jobData.id) });
    } catch (error) {
      console.error('Error handling reject submit:', error);
      showErrorToast('Rejection failed', 'Something went wrong. Please try again.');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {getStatusBadge(status)}
          <Badge variant="outline" className={cn('gap-1', getPriorityColor(jobData.priority))}>
            <AlertCircle className="size-3 shrink-0" />
            {jobData.priority.charAt(0).toUpperCase() + jobData.priority.slice(1)} Priority
          </Badge>
          <MirStatusBadge
            mirPendingDirection={jobData.mirPendingDirection}
            mirConversationHistory={jobData.mirConversationHistory}
            workOrderStatus={jobData.status}
          />
          <PSRBadge isVulnerable={jobData.isVulnerable ?? false} />
          {jobData.hasBeenResent && (
            <Badge className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/20 gap-1 text-nowrap">
              <RefreshCcw className="size-3 shrink-0" />
              Re-sent with Info
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Action buttons for rejected and more-info-requested statuses */}
          {(status === 'rejected' || status === 'more-info-requested') && (
            <>
              <Button size="sm" onClick={() => setAcceptModalOpen(true)}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setRejectModalOpen(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </>
          )}

          {/* Waiting notice for Validation Requested status */}
          {jobData.status === 'Validation Requested' &&
            clientConfig.features.coreValidationWaitingNotice && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarClock className="w-4 h-4 shrink-0" />
                <span>
                  Waiting on CORE to approve and complete the job based on your validation request.
                </span>
              </div>
            )}

          {/* Validation Request button for Engineering Completed status */}
          {jobData.status === 'Engineering Completed' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => setValidationRequestedModalOpen(true)}
              >
                Validation Request
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Simulate Mobile Engineering Complete button + Abort for On Site status */}
          {jobData.status === 'On Site' && (
            <>
              {technicalCompletionGate.blocked && (
                <PermitGateNotice reason={technicalCompletionGate.reason} />
              )}
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={technicalCompletionGate.blocked}
                title={technicalCompletionGate.blocked ? technicalCompletionGate.reason : undefined}
                onClick={() => setEngineeringCompleteModalOpen(true)}
              >
                Simulate Mobile Engineering Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setAbortModalOpen(true)}
              >
                Abort
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Cancel and Replan buttons for Abort/Aborted state */}
          {jobData.status === 'Aborted' && jobData.jobStatusName === 'Abort' && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setCancelFromAbortModalOpen(true)}
              >
                Cancel
              </Button>
              <Button variant="outline" size="sm" onClick={() => setReplanFromAbortModalOpen(true)}>
                Replan
              </Button>
            </>
          )}

          {/* Cancelled state notice */}
          {jobData.status === 'Cancelled' && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <XCircle className="w-4 h-4 shrink-0" />
              <span>This job has been cancelled and cannot be worked on.</span>
            </div>
          )}

          {/* Simulate Mobile On Site button for En Route status */}
          {jobData.status === 'En Route' && (
            <>
              <Button
                size="sm"
                disabled={engineerOnSite.isPending}
                onClick={() =>
                  engineerOnSite.mutate(Number(jobData.id), {
                    onSuccess: () => {
                      showSuccessToast('Engineer on site');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                      });
                    },
                  })
                }
              >
                {engineerOnSite.isPending ? 'Sending…' : 'Simulate Mobile On Site'}
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Simulate Mobile En Route button for Planned status */}
          {jobData.status === 'Planned' && (
            <>
              {enRouteGate.blocked && <PermitGateNotice reason={enRouteGate.reason} />}
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={engineerEnRoute.isPending || enRouteGate.blocked}
                title={enRouteGate.blocked ? enRouteGate.reason : undefined}
                onClick={() =>
                  engineerEnRoute.mutate(Number(jobData.id), {
                    onSuccess: () => {
                      showSuccessToast('Engineer en route');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                      });
                    },
                  })
                }
              >
                {engineerEnRoute.isPending ? 'Sending…' : 'Simulate Mobile En Route'}
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Schedule + Cancel buttons for Ready for Planning status */}
          {jobData.status === 'Ready for Planning' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => navigate('/schedule')}
              >
                <CalendarClock className="w-4 h-4 mr-2" />
                Plan
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Action buttons for new and reopened status */}
          {(status === 'new' || status === 'reopened') && (
            <>
              <Button size="sm" onClick={() => setAcceptModalOpen(true)}>
                <CheckCircle2 className="size-4 mr-2" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setRejectModalOpen(true)}
              >
                <XCircle className="size-4 mr-2" />
                Reject
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Cancel button for Duration Challenged status (no other actions available) */}
          {js === 'On Hold' && wos === 'Duration Challenged' && (
            <CancelButton onClick={() => setCancelModalOpen(true)} />
          )}

          <RequestMoreInfoButton
            mirPendingDirection={jobData.mirPendingDirection}
            workOrderStatus={jobData.status}
            onClick={() => setRequestInfoModalOpen(true)}
          />
        </div>
      </div>

      {/* Modals */}
      <RejectPackageModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={handleRejectSubmit}
        isSubmitting={isUploadingFiles || rejectWorkOrders.isPending}
        mode="reject"
        targetName={jobData.name ?? 'Error loading name'}
      />

      <RequestMoreInfoOrReplyModal
        open={requestInfoModalOpen}
        onOpenChange={setRequestInfoModalOpen}
        job={requestInfoJob}
        isSubmitting={
          isUploadingFiles || (isReply ? replyMoreInfo.isPending : requestMoreInfo.isPending)
        }
        onSubmit={handleRequestInfoSubmit}
      />

      <ValidationRequestModal
        open={validationRequestedModalOpen}
        onOpenChange={setValidationRequestedModalOpen}
        isSubmitting={validationRequested.isPending}
        onSubmit={({ note, invoiceReference, invoiceAmount }) => {
          validationRequested.mutate(
            {
              id: Number(jobData.id),
              note,
              invoiceReference,
              invoiceAmount,
            },
            {
              onSuccess: (data) => {
                if (!data.result.success) {
                  showErrorToast(
                    'Validation Request Failed',
                    'This job cannot be submitted for validation until all mandatory forms are completed.',
                  );
                  return;
                }
                setValidationRequestedModalOpen(false);
                showSuccessToast('Validation request submitted');
                queryClient.invalidateQueries({
                  queryKey: queryKeys.jobs.detail(jobData.id),
                });
                queryClient.invalidateQueries({
                  queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                });
              },
            },
          );
        }}
      />

      <Dialog
        open={engineeringCompleteModalOpen}
        onOpenChange={(open) => {
          setEngineeringCompleteModalOpen(open);
          if (!open) setEngineeringCompleteNote('');
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Engineering Complete</DialogTitle>
            <DialogDescription>Add a note to complete this job.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Note <span className="text-red-600">*</span>
            </label>
            <Textarea
              placeholder="Describe the work completed..."
              value={engineeringCompleteNote}
              onChange={(e) => setEngineeringCompleteNote(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEngineeringCompleteModalOpen(false);
                setEngineeringCompleteNote('');
              }}
              disabled={engineeringComplete.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={!engineeringCompleteNote.trim() || engineeringComplete.isPending}
              onClick={() => {
                engineeringComplete.mutate(
                  { id: Number(jobData.id), note: engineeringCompleteNote.trim() },
                  {
                    onSuccess: () => {
                      setEngineeringCompleteModalOpen(false);
                      setEngineeringCompleteNote('');
                      showSuccessToast('Engineering complete');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                      });
                    },
                  },
                );
              }}
            >
              {engineeringComplete.isPending ? 'Submitting…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={abortModalOpen}
        onOpenChange={(open) => {
          setAbortModalOpen(open);
          if (!open) setAbortNote('');
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Abort Job</DialogTitle>
            <DialogDescription>Are you sure you want to abort this job?</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Note <span className="text-red-600">*</span>
            </label>
            <Textarea
              placeholder="Describe the reason for aborting..."
              value={abortNote}
              onChange={(e) => setAbortNote(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAbortModalOpen(false);
                setAbortNote('');
              }}
              disabled={abortWorkOrder.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              variant="outline"
              disabled={!abortNote.trim() || abortWorkOrder.isPending}
              onClick={() => {
                abortWorkOrder.mutate(
                  { id: Number(jobData.id), note: abortNote.trim() },
                  {
                    onSuccess: () => {
                      setAbortModalOpen(false);
                      setAbortNote('');
                      showSuccessToast('Job aborted');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                      });
                    },
                  },
                );
              }}
            >
              {abortWorkOrder.isPending ? 'Aborting…' : 'Abort'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelFromAbortModalOpen} onOpenChange={setCancelFromAbortModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancel Job</DialogTitle>
            <DialogDescription>
              This will send a cancellation event to CORE and set the job status to Cancelled. Are
              you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelFromAbortModalOpen(false)}
              disabled={cancelFromAbort.isPending}
            >
              Back
            </Button>
            <Button
              type="button"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              variant="outline"
              disabled={cancelFromAbort.isPending}
              onClick={() => {
                cancelFromAbort.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setCancelFromAbortModalOpen(false);
                    showSuccessToast('Job cancelled');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                    });
                  },
                });
              }}
            >
              {cancelFromAbort.isPending ? 'Cancelling…' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={replanFromAbortModalOpen} onOpenChange={setReplanFromAbortModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Replan Job</DialogTitle>
            <DialogDescription>
              This will set the job status back to Ready for Planning. No event will be sent to
              CORE. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReplanFromAbortModalOpen(false)}
              disabled={replanFromAbort.isPending}
            >
              Back
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={replanFromAbort.isPending}
              onClick={() => {
                replanFromAbort.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setReplanFromAbortModalOpen(false);
                    showSuccessToast('Job set to Ready for Planning');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                    });
                  },
                });
              }}
            >
              {replanFromAbort.isPending ? 'Replanning…' : 'Confirm Replan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancel Job</DialogTitle>
            <DialogDescription>
              Warning: Cancelling this job is permanent. It cannot be replanned or recovered. Are
              you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelModalOpen(false)}
              disabled={cancelWorkOrderMutation.isPending}
            >
              Go Back
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              disabled={cancelWorkOrderMutation.isPending}
              onClick={() => {
                cancelWorkOrderMutation.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setCancelModalOpen(false);
                    showSuccessToast('Job Cancelled');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                    });
                  },
                });
              }}
            >
              {cancelWorkOrderMutation.isPending ? 'Cancelling…' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Accept Job</DialogTitle>
            <DialogDescription>Are you sure you want to accept this job?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAcceptModalOpen(false)}
              disabled={acceptWorkOrders.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={acceptWorkOrders.isPending}
              onClick={() => {
                acceptWorkOrders.mutate([Number(jobData.id)], {
                  onSuccess: (resp) => {
                    setAcceptModalOpen(false);
                    const results = resp.results ?? [];
                    const failed = results.filter((r: { success: boolean }) => !r.success);
                    if (failed.length === 0) {
                      showSuccessToast('Job accepted');
                    }
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.milestoneHistory(jobData.id),
                    });
                  },
                });
              }}
            >
              {acceptWorkOrders.isPending ? 'Accepting…' : 'Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
